import { useParams } from 'react-router-dom';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchBackButton } from '@/components/ui/search-back-button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { ActivityTimeline } from '@/components/ActivityTimeline';
import { useOrder } from '@/hooks/useOrder';

function AddressBlock({
  title,
  address,
}: {
  title: string;
  address: Record<string, unknown> | null;
}) {
  if (!address) return null;
  const a = address as Record<string, string>;
  return (
    <div>
      <label className="text-xs text-muted-foreground uppercase tracking-wider">{title}</label>
      <div className="mt-1 space-y-0.5 break-words text-sm text-foreground">
        {(a.first_name || a.last_name) && (
          <p>
            {a.first_name} {a.last_name}
          </p>
        )}
        {a.address1 && <p>{a.address1}</p>}
        {a.address2 && <p>{a.address2}</p>}
        <p>
          {[a.city, a.province, a.zip].filter(Boolean).join(', ')}
        </p>
        {a.country && <p>{a.country}</p>}
      </div>
    </div>
  );
}

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading, error } = useOrder(id);

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="h-14 flex items-center gap-3 border-b border-border px-4 shrink-0 md:px-6">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-5 w-32" />
        </div>
        <div className="flex-1 space-y-6 overflow-auto p-4 md:p-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="h-full flex flex-col">
        <div className="h-14 flex items-center gap-3 border-b border-border px-4 shrink-0 md:px-6">
          <SearchBackButton />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Order not found</p>
        </div>
      </div>
    );
  }

  const lineItems = order.line_items ?? [];
  const paymentVariant =
    order.financial_status === 'paid'
      ? 'success'
      : order.financial_status === 'refunded'
        ? 'destructive'
        : 'warning';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex min-h-14 flex-wrap items-center gap-2 border-b border-border px-4 py-2 shrink-0 md:px-6">
        <SearchBackButton />
        <h1 className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
          {order.name ?? `Order #${order.order_number}`}
        </h1>
        {order.financial_status && (
          <Badge variant={paymentVariant} className="capitalize">
            {order.financial_status}
          </Badge>
        )}
        {order.fulfillment_status ? (
          <Badge
            variant={order.fulfillment_status === 'fulfilled' ? 'success' : 'warning'}
            className="capitalize"
          >
            {order.fulfillment_status}
          </Badge>
        ) : (
          <Badge variant="outline">Unfulfilled</Badge>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 space-y-6 overflow-auto p-4 md:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Order summary */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-sm">Order Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">
                    Order Date
                  </label>
                  <p className="text-sm text-foreground mt-1">
                    {formatDateTime(order.processed_at ?? order.shopify_created_at)}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">
                    Customer
                  </label>
                  <p className="text-sm text-foreground mt-1">
                    {order.customer_email ?? '\u2014'}
                  </p>
                </div>
              </div>
              {order.note && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">
                    Note
                  </label>
                  <p className="text-sm text-foreground mt-1">{order.note}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Financial summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Financial Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground font-mono">
                  {formatCurrency(order.subtotal_price)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span className="text-foreground font-mono">
                  {formatCurrency(order.total_tax)}
                </span>
              </div>
              {(order.total_discounts ?? 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discounts</span>
                  <span className="text-success font-mono">
                    -{formatCurrency(order.total_discounts)}
                  </span>
                </div>
              )}
              <div className="border-t border-border pt-2 flex justify-between text-sm font-medium">
                <span className="text-foreground">Total</span>
                <span className="text-foreground font-mono">
                  {formatCurrency(order.total_price)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Addresses */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {order.shipping_address && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Shipping Address</CardTitle>
              </CardHeader>
              <CardContent>
                <AddressBlock title="" address={order.shipping_address} />
              </CardContent>
            </Card>
          )}
          {order.billing_address && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Billing Address</CardTitle>
              </CardHeader>
              <CardContent>
                <AddressBlock title="" address={order.billing_address} />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Line Items */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Line Items ({lineItems.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="space-y-2 p-3 md:hidden">
              {lineItems.map((item) => (
                <div key={item.id} className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="min-w-0">
                    <p className="line-clamp-2 text-sm font-medium text-foreground">
                      {item.title}
                    </p>
                    {item.variant_title && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {item.variant_title}
                      </p>
                    )}
                    <p className="mt-0.5 truncate text-xs font-mono text-muted-foreground">
                      SKU {item.sku ?? '—'}
                    </p>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <div className="rounded border border-border bg-background p-2">
                      <p className="mb-1 text-[10px] font-medium uppercase text-muted-foreground">
                        Qty
                      </p>
                      <p className="text-sm font-mono text-foreground">{item.quantity}</p>
                    </div>
                    <div className="rounded border border-border bg-background p-2">
                      <p className="mb-1 text-[10px] font-medium uppercase text-muted-foreground">
                        Price
                      </p>
                      <p className="text-sm font-mono text-foreground">
                        {formatCurrency(item.price)}
                      </p>
                    </div>
                    <div className="rounded border border-border bg-background p-2">
                      <p className="mb-1 text-[10px] font-medium uppercase text-muted-foreground">
                        Total
                      </p>
                      <p className="text-sm font-mono text-foreground">
                        {formatCurrency(
                          item.price != null ? item.price * item.quantity : null,
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Table wrapperClassName="hidden md:block">
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <p className="text-sm text-foreground">{item.title}</p>
                        {item.variant_title && (
                          <p className="text-xs text-muted-foreground">{item.variant_title}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {item.sku ?? '\u2014'}
                    </TableCell>
                    <TableCell className="text-right text-sm font-mono">{item.quantity}</TableCell>
                    <TableCell className="text-right text-sm font-mono">
                      {formatCurrency(item.price)}
                    </TableCell>
                    <TableCell className="text-right text-sm font-mono">
                      {formatCurrency(
                        item.price != null ? item.price * item.quantity : null,
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Activity timeline */}
        <ActivityTimeline orderId={order.shopify_order_id} />
      </div>
    </div>
  );
}
